import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { GetAwsAccountResponse } from "@microtica/ms-aws-sdk";
import type { GetStagesItem } from "@microtica/ms-engine-sdk";
import { getCloudService, getCostOptimizationService, getEnvironmentService } from "../../../backend";
import type { CostExplorerFilters } from "../../../backend/cost-optimizer";

export interface CostExplorerMetadata {
    accounts: GetAwsAccountResponse[];
    environments: GetStagesItem[];
}

export const costExplorerKeys = {
    all: ["cost-explorer"] as const,
    metadata: (projectId: string) => [...costExplorerKeys.all, projectId, "metadata"] as const,
    data: (projectId: string, filters: CostExplorerFilters) =>
        [...costExplorerKeys.all, projectId, "data", filters] as const
};

export function useCostExplorerMetadata(projectId: string, enabled = true) {
    return useQuery<CostExplorerMetadata>({
        queryKey: costExplorerKeys.metadata(projectId),
        queryFn: async () => {
            const [accountsResponse, environmentsResponse] = await Promise.all([
                getCloudService().getAwsAccounts(projectId),
                getEnvironmentService().getStages(projectId)
            ]);
            return {
                accounts: accountsResponse.data.awsAccounts,
                environments: environmentsResponse.data.stages.filter(environment => environment.cloudProvider === "aws")
            };
        },
        enabled: Boolean(projectId && enabled),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000
    });
}

export function useCostExplorer(projectId: string, filters: CostExplorerFilters, enabled = true) {
    return useQuery({
        queryKey: costExplorerKeys.data(projectId, filters),
        queryFn: async () => (await getCostOptimizationService().getCostExplorer(projectId, filters)).data,
        enabled: Boolean(projectId && filters.accountIds.length && enabled),
        placeholderData: keepPreviousData,
        staleTime: 5 * 60 * 1000,
        retry: 1
    });
}
